"use client";
import "./style.css";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";

// import required modules
import { Autoplay, Navigation } from "swiper/modules";

import AutoCarousel from "@/src/components/AutoCarousel/AutoCarousel";
import CustomHeader from "@/src/components/header/header";
import Footer from "@/src/components/footer/Footer";

import heroShape from "@/src/assets/images/driverPartner/heroShape.svg";
import Iphone15 from "@/src/assets/images/driverPartner/iPhone15Pro.webp";
import playStore from "@/src/assets/images/driverPartner/playStore.svg";
import appStore from "@/src/assets/images/driverPartner/appStore.svg";
import Quote from "@/src/assets/images/driverPartner/quote.svg";
import Driver1 from "@/src/assets/images/driverPartner/driver/driver1.webp";
import Driver2 from "@/src/assets/images/driverPartner/driver/driver2.webp";
import Driver3 from "@/src/assets/images/driverPartner/driver/driver3.webp";

import workHour from "@/src/assets/images/driverPartner/expandable/workHour.svg";
import newVehicle from "@/src/assets/images/driverPartner/expandable/newVehicle.webp";
import group from "@/src/assets/images/driverPartner/expandable/group.webp";
import pay from "@/src/assets/images/driverPartner/expandable/pay.webp";
import plant from "@/src/assets/images/driverPartner/expandable/plant.webp";
import Link from "next/link";
import DriverPartnerExpandableEle from "@/src/components/DriverPartnerExpandableEle/DriverPartnerExpandableEle";

const driversStory = [
	{
		driverName: "Shiv",
		story: "As a driver partner, ZEVO offers unmatched flexibility and seamless support. The user-friendly app, competitive earnings, and diverse rider interactions make it more than a job—it's a rewarding journey. Proud to be part of a community where driving is about empowerment and meaningful connections.",
		image: Driver1.src,
	},
	{
		driverName: "Ravi",
		story: "Working with ZEVO has been a game-changer for me. Not only do I get to drive the latest electric vehicles, but I'm also contributing to a greener future. The earnings are more than decent; they're impressive! I love being part of a company that values sustainability and innovation while providing a fulfilling income. With ZEVO, I've experienced a level of job satisfaction I never thought possible. ",
		image: Driver2.src,
	},
	{
		driverName: "Sohail",
		story: "ZEVO has opened doors for me that I never knew existed. The flexibility to manage my schedule combined with the opportunity to drive cutting-edge EVs is incredible. The earnings are solid, but what excites me most is the entrepreneurial spirit ZEVO fosters. It's not just a driving gig; it's a chance to grow within a forward-thinking industry.",
		image: Driver3.src,
	},
];

function page() {
	const [swiper, setSwiper] = useState(null);
	const [associateActiveSlide, setAssociateActiveSlide] = useState(0);
	const [inView, setInView] = useState(false);
	const ref = useRef(null);
	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) setInView(entry.isIntersecting);
			},
			{ threshold: 0.1 },
		);

		if (ref.current) {
			observer.observe(ref.current);
		}

		return () => {
			if (ref.current) {
				observer.unobserve(ref.current);
			}
		};
	}, [ref]);

	const expandableData = [
		{
			iconUrl: workHour,
			text: "Flexible work hours & real-time job notifications",
		},
		{
			iconUrl: pay,
			text: "Competitive pay with earning tracking dashboard",
		},
		{
			iconUrl: newVehicle,
			text: "The opportunity to drive brand new electric vehicles",
		},
		{
			iconUrl: plant,
			text: "Making a positive impact on the environment",
		},
		{
			iconUrl: group,
			text: "Being part of a growing and supportive team",
		},
	];
	const [script, setScript] = useState();
	useEffect(() => {
		setScript(
			JSON.stringify({
				"@context": "https://schema.org",
				"@type": "FAQPage",
				mainEntity: [
					{
						"@type": "Question",
						name: "Who can become a Zevo driver partner?",
						acceptedAnswer: {
							"@type": "Answer",
							text: "Zevo seeks passionate and reliable drivers with a valid commercial driver's license (CDL) and a clean driving record.",
						},
					},
					{
						"@type": "Question",
						name: "What are the benefits of becoming a Zevo driver partner?",
						acceptedAnswer: {
							"@type": "Answer",
							text: "Zevo drivers enjoy flexible work hours, real-time job notifications, competitive pay with an earnings tracking dashboard, the opportunity to drive brand new electric vehicles, and contribute to a sustainable future. They are also part of a growing and supportive team.",
						},
					},
					{
						"@type": "Question",
						name: "How do I become a Zevo driver partner?",
						acceptedAnswer: {
							"@type": "Answer",
							text: "The process involves downloading the Zevo app, creating an account, completing the driver application (including basic information, CDL upload, and answering questions), and waiting for approval. Once approved, drivers can start accepting jobs, manage their schedule, and track earnings within the app.",
						},
					},
				],
			}),
		);
	});
	return (
		<>
			<script type="application/ld+json">{script}</script>
			<CustomHeader />
			<div className="driver_partner_container">
				<section className="section1">
					<div className="driver_partner_hero">
						<div className="hero_left">
							<h2 className="normal">JOIN THE EVOLUTION</h2>
							<h1 className="green">BECOME A ZEVO DRIVER</h1>
							<p>
								If you're a passionate and reliable driver with
								a valid commercial driver's license (CDL) and a
								clean driving record, then ZEVO wants you!
							</p>
						</div>
					</div>
					<Image
						className="heroShape"
						src={heroShape}
						width={735}
						height={657}
						alt="hero shape"
					/>
				</section>
				<section className="section2">
					<div className="company_intro_container">
						<p>
							ZEVO is India's leading electric vehicle supply
							chain platform, and we're seeking dedicated drivers
							to help us deliver goods across the country in a
							sustainable way.
						</p>
						<p>As a ZEVO driver, you'll enjoy:</p>
					</div>
					<DriverPartnerExpandableEle
						expandableData={expandableData}
					/>
				</section>
				<section className="section3">
					<h2>How to Join us</h2>
					<div>
						<Image
							className="iphone_img"
							src={Iphone15}
							width={765}
							height={383}
							alt="Iphone"
						/>
						<div className="section3_app_details_container">
							<div className="storesContainer download_links_img">
								<Link
									href="https://apps.apple.com/in/app/zevo-pilot-app/id6477534578"
									rel="noopener noreferrer canonical"
									target="_blank"
								>
									<Image
										className=""
										src={appStore}
										height={50}
										width={0}
										alt="app store"
									/>
								</Link>
								<Link
									href="https://play.google.com/store/apps/details?id=in.zevoindia.app"
									rel="noopener noreferrer"
									target="_blank"
								>
									<Image
										className=""
										src={playStore}
										height={50}
										width={0}
										alt="play store"
									/>
								</Link>
							</div>
							<div className="section3_animation_card" ref={ref}>
								<AutoCarousel autoplay={inView} />
							</div>
						</div>
						<div className="app_link_container">
							<Image
								src={Iphone15}
								className="mobile_iphone"
								width={1000}
								height={0}
								alt="iphone"
							/>
							<div>
								<Link
									href="https://apps.apple.com/in/app/zevo-pilot-app/id6477534578"
									rel="noopener noreferrer"
									target="_blank"
								>
									<Image
										className="store_link_img"
										src={appStore}
										width={130}
										height={0}
										alt="play store"
									/>
								</Link>
								<Link
									href="https://play.google.com/store/apps/details?id=in.zevoindia.app"
									rel="noopener noreferrer"
									target="_blank"
								>
									<Image
										className="store_link_img"
										src={playStore}
										width={130}
										height={0}
										alt="app store"
									/>
								</Link>
							</div>
						</div>
					</div>
				</section>
				<section className="section4">
					<h2>Driver’s stories</h2>
					<div className="driver_stories_container">
						<div className="story_card_mobile">
							<Image
								className="quote_icon"
								src={Quote}
								width={160}
								height={100}
								alt="quote"
							/>
							<Swiper
								initialSlide={0}
								autoplay={{
									delay: 3000,
									pauseOnMouseEnter: true,
								}}
								onSwiper={setSwiper}
								loop={true}
								speed={800}
								navigation={true}
								modules={[Navigation, Autoplay]}
								className="mobile_driver_swiper"
								onInit={function () {
									for (
										let i = 0;
										i < this.slides.length;
										i++
									) {
										if (i !== this.activeIndex) {
											this.slides[i].classList.add(
												"swiper-slide-inactive",
											);
										}
									}
									if (this.isBeginning) {
										document.querySelector(
											".associate_swiper_cards_nav_prev",
										).style.display = "none";
									}
								}}
								onSlideChange={function (e) {
									setAssociateActiveSlide(e.activeIndex);
									for (
										let i = 0;
										i < this.slides.length;
										i++
									) {
										if (i !== this.activeIndex) {
											this.slides[i].classList.add(
												"swiper-slide-inactive",
											);
										} else {
											this.slides[i].classList.remove(
												"swiper-slide-inactive",
											);
										}
									}
									document.querySelector(
										".associate_swiper_cards_nav_prev",
									).style.display = this.isBeginning
										? "none"
										: "flex";
									document.querySelector(
										".associate_swiper_cards_nav_next",
									).style.display = this.isEnd
										? "none"
										: "flex";
								}}
							>
								{driversStory &&
									driversStory.map((ele, i) => {
										return (
											<SwiperSlide key={i}>
												<div
													className="image_container"
													style={{
														backgroundImage: `url(${ele.image})`,
													}}
												></div>
												<div className="slide_info_div">
													<h4>{ele.driverName}</h4>
													<p>{ele.story}</p>
												</div>
											</SwiperSlide>
										);
									})}
							</Swiper>
							<div className="associate_swiper_cards_nav">
								<button
									className="associate_swiper_cards_nav_prev"
									onClick={() => {
										swiper.slideTo(
											associateActiveSlide - 1,
										);
									}}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
									>
										<path
											fillRule="evenodd"
											clipRule="evenodd"
											d="M12.0727 2.6941C11.7118 3.0726 11.7264 3.67165 12.1053 4.03212L19.4897 11.0577H2.14815C1.62493 11.0577 1.20078 11.4815 1.20078 12.0041C1.20078 12.5268 1.62493 12.9506 2.14815 12.9506H19.4818L12.1053 19.9687C11.7264 20.3291 11.7118 20.9282 12.0727 21.3067C12.4335 21.6852 13.0332 21.6998 13.412 21.3393L22.4585 12.7324C22.6676 12.5588 22.8008 12.297 22.8008 12.0041L22.8008 12.0004C22.8009 11.7502 22.7024 11.5005 22.5068 11.3143L13.412 2.66147C13.0332 2.301 12.4335 2.31561 12.0727 2.6941Z"
											fill="white"
										/>
									</svg>
								</button>
								<button
									className="associate_swiper_cards_nav_next"
									onClick={() => {
										swiper.slideTo(
											associateActiveSlide + 1,
										);
									}}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
									>
										<path
											fillRule="evenodd"
											clipRule="evenodd"
											d="M12.0727 2.6941C11.7118 3.0726 11.7264 3.67165 12.1053 4.03212L19.4897 11.0577H2.14815C1.62493 11.0577 1.20078 11.4815 1.20078 12.0041C1.20078 12.5268 1.62493 12.9506 2.14815 12.9506H19.4818L12.1053 19.9687C11.7264 20.3291 11.7118 20.9282 12.0727 21.3067C12.4335 21.6852 13.0332 21.6998 13.412 21.3393L22.4585 12.7324C22.6676 12.5588 22.8008 12.297 22.8008 12.0041L22.8008 12.0004C22.8009 11.7502 22.7024 11.5005 22.5068 11.3143L13.412 2.66147C13.0332 2.301 12.4335 2.31561 12.0727 2.6941Z"
											fill="white"
										/>
									</svg>
								</button>
							</div>
						</div>
					</div>
				</section>
			</div>
			<Footer />
		</>
	);
}

export default page;
